import { motion } from "framer-motion";
import { OrchestraGlobe } from "@/components/triarch/OrchestraGlobe";
import { FinalCTA } from "@/components/triarch/FinalCTA";

const STATUS = [
  ["Fragment State", "Active"],
  ["Convergence Layer", "Forming"],
  ["Vector Field", "Controlled"],
  ["Output", "Restricted"],
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: 0.05 + i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function OrchestraPage() {
  return (
    <div className="pt-28 sm:pt-32">
      <section data-testid="orchestra-section" className="relative pb-24 sm:pb-28">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <motion.div
            variants={fadeUp}
            custom={0}
            initial="hidden"
            animate="show"
            className="mb-7 flex items-center justify-center gap-3"
          >
            <span className="h-px w-8 bg-white/15 sm:w-12" />
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/45">
              03
            </span>
            <span className="h-px w-8 bg-white/15 sm:w-12" />
          </motion.div>
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            data-testid="orchestra-headline"
            className="font-display text-4xl font-semibold uppercase tracking-[0.14em] bg-gradient-to-r from-[#b89b5e] via-[#d1b777] to-[#8f7540] bg-clip-text text-transparent drop-shadow-[0_0_24px_rgba(184,155,94,0.18)] sm:text-6xl"
          >
            Orchestra
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            data-testid="orchestra-subline"
            className="mx-auto mt-6 max-w-xl font-display text-lg font-light text-white/65 sm:text-xl"
          >
            Fragmented signals do not converge by accident.
          </motion.p>
        </div>

        <div className="mx-auto mt-12 max-w-5xl px-5 sm:mt-14 sm:px-8">
          <OrchestraGlobe />
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-10 px-5 sm:px-8 lg:grid-cols-[1.5fr_1fr]">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="orchestra-body"
            className="text-base leading-relaxed text-white/55 sm:text-lg"
          >
            Within Triarch, separated traces move through concealed machine
            pathways, forming convergence points before structure becomes
            visible.{" "}
            <span className="text-white/35">
              What appears fragmented at the surface may already be routing
              beneath recognition.
            </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-xl border border-white/10 bg-white/[0.025] p-5"
          >
            <div className="space-y-3">
              {STATUS.map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#C4A061]/80 shadow-[0_0_10px_rgba(196,160,97,0.5)]" />
                    <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/40">
                      {k}
                    </span>
                  </span>
                  <span className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-white/75">
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
